// Reusable "Filtered by nest: <code> — <name> ×" banner for admin
// pages that accept a ?nest=<slug> deep-link from the sidebar Nest
// stats (Rules, File workflows). Reads /nests to translate the slug
// into the human-friendly code + name; falls back to just the slug
// if the nest list hasn't loaded yet. Calls onClear when the user
// dismisses it (the parent removes ?nest= from the URL).

import { useQuery } from '@tanstack/react-query';
import type { Nest } from '@captain-tasko/shared';
import { apiList } from '../lib/api.js';

export function NestFilterBanner({
  slug,
  onClear,
}: {
  slug: string;
  onClear: () => void;
}) {
  const nestsQ = useQuery({
    queryKey: ['nests'],
    queryFn: () => apiList<Nest>('/nests'),
    staleTime: 60_000,
  });
  const nest = (nestsQ.data?.data ?? []).find((n) => n.slug === slug);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        marginBottom: 12,
        background: 'var(--surface-sunken)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        fontSize: 12,
        color: 'var(--text-secondary)',
      }}
    >
      <span style={{ color: 'var(--text-muted)' }}>Filtered by nest:</span>
      {nest ? (
        <>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 26,
              padding: '1px 5px',
              background: 'color-mix(in srgb, var(--accent-primary) 14%, transparent)',
              color: 'var(--accent-primary)',
              borderRadius: 3,
              fontSize: 10,
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              letterSpacing: '.04em',
            }}
          >
            {nest.code}
          </span>
          <strong style={{ color: 'var(--text-primary)' }}>{nest.name}</strong>
        </>
      ) : (
        <strong style={{ color: 'var(--text-primary)' }}>{slug}</strong>
      )}
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear nest filter"
        title="Clear nest filter"
        style={{
          marginLeft: 'auto',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          fontSize: 14,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
